import React from "react";
import Preloader from "../../../Preloader/Preloader";
import { getRpGpLaborEffortId } from "../../../../api/paymentsProgressApi";
import {
  ApprovalStatusBadge,
  getApprovalStatusText,
} from "./paymentsApprovalStatus";

const GROUP_BY_USERS = "users";

/**
 * Деньги по секундам отчётов, опираясь только на поля бэкенда:
 * labour_cost — бюджет трудозатрат в секундах (на всю смету amount).
 * Тогда «цена секунды» = amount / labour_cost → сумма человека = seconds × (amount / labour_cost).
 */
const moneyFromSecondsUsingLabourCost = (secondsSpend, amountTg, labourCostSeconds) => {
  const seconds = Number(secondsSpend) || 0;
  const amount = Number(amountTg);
  const labour = Number(labourCostSeconds);
  if (!labour || labour <= 0 || Number.isNaN(labour) || !amount || amount <= 0 || Number.isNaN(amount)) {
    return null;
  }
  return seconds * (amount / labour);
};

const GeneralDesignWorkDetails = ({
  row,
  user,
  userName,
  userKey,
  userReportsData,
  loadingReports,
  savingId,
  setSavingId,
  setConfirmModal,
  setCommentModal,
  updateGeneralDesignCorrection,
  setGeneralReportsByKey,
  formatMoney,
  month,
  year,
  isLockedByApproval,
  isReadOnly,
}) => {
  if (loadingReports) {
    return (
      <tr>
        <td colSpan={10} className="pp-loading-cell">
          <Preloader />
        </td>
      </tr>
    );
  }

  const correction = userReportsData?.correction || {};
  const rowSectionName = row?.section?.section?.name || "—";
  const contractSectionsId = getRpGpLaborEffortId(row);
  const correctionPct = correction?.completion_percentage_after_correction ?? null;
  const correctionSum = correction?.completion_sum_after_correction ?? null;
  const correctionComment = correction?.comment || "—";
  const correctionSectionName = rowSectionName || "Корректировка";
  const estimateContract = row?.amount ?? row?.section?.amount ?? 0;
  const alreadyPayed = row?.already_payed;
  const labourBudgetSeconds = Number(row?.labour_cost);
  const reports = Array.isArray(userReportsData?.reports) ? userReportsData.reports : [];
  const totalSecondsByEmployee = reports.reduce(
    (sum, report) => sum + (Number(report?.seconds_spend) || 0),
    0,
  );
  const toBePayedByEmployee = moneyFromSecondsUsingLabourCost(
    totalSecondsByEmployee,
    estimateContract,
    labourBudgetSeconds,
  );
  const completionPercentByEmployee =
    toBePayedByEmployee != null && Number(estimateContract) > 0
      ? (toBePayedByEmployee / Number(estimateContract)) * 100
      : null;
  const estimateContractNum = Number(estimateContract) || 0;
  const alreadyPayedNum = Number(alreadyPayed) || 0;
  const remainingAmount = estimateContractNum - alreadyPayedNum;
  const toBePayedByEmployeeNum = Number(toBePayedByEmployee);
  const correctionSumNum = Number(correctionSum);
  const isEmployeePayoutOverBudget =
    estimateContractNum > 0 &&
    Number.isFinite(toBePayedByEmployeeNum) &&
    alreadyPayedNum + toBePayedByEmployeeNum > estimateContractNum;
  const isCorrectionPayoutOverBudget =
    estimateContractNum > 0 &&
    Number.isFinite(correctionSumNum) &&
    alreadyPayedNum + correctionSumNum > estimateContractNum;
  const maxAllowedPayout = Math.max(0, estimateContractNum - alreadyPayedNum);
  const isSavingGeneralCorrection =
    savingId === `general-design-${contractSectionsId}-${user.id}`;

  const applyCorrection = (payload) => {
    setSavingId(`general-design-${contractSectionsId}-${user.id}`);
    updateGeneralDesignCorrection(payload)
      .then((updatedCorrection) => {
        setGeneralReportsByKey((prev) => ({
          ...prev,
          [userKey]: {
            correction: {
              ...(prev[userKey]?.correction || {}),
              ...updatedCorrection,
            },
            reports: prev[userKey]?.reports || [],
          },
        }));
      })
      .catch((err) =>
        alert(err?.message || "Ошибка сохранения корректировки"),
      )
      .finally(() => setSavingId(null));
  };

  const lockTitle = isLockedByApproval
    ? "Сотрудник отправлен на согласование — редактирование запрещено"
    : undefined;

  return (
    <>
      <tr className="pp-detail-header-row">
        <th>Раздел</th>
        <th className="pp-money">Смета по договору</th>
        <th className="pp-money">Выплачено</th>
        <th className="pp-money">Остаток</th>
        <th>% доля сотрудника</th>
        <th>% выполнения (по данным от сотрудника)</th>
        <th className="pp-money">К выплате (по данным от сотрудника)</th>
        <th>% выполнения (корректировка руководителя)</th>
        <th className="pp-money">К выплате (корректировка руководителя)</th>
        <th>Комментарий</th>
      </tr>
      <tr className="pp-work-row">
        <td>{correctionSectionName}</td>
        <td className="pp-money">₸ {formatMoney(estimateContract)}</td>
        <td className="pp-money">
          {alreadyPayed == null ? "—" : `₸ ${formatMoney(alreadyPayed)}`}
        </td>
        <td className="pp-money">₸ {formatMoney(remainingAmount)}</td>
        <td>—</td>
        <td>
          {completionPercentByEmployee == null
            ? "—"
            : `${completionPercentByEmployee.toFixed(2)}%`}
        </td>
        <td
          className="pp-money"
          style={
            isEmployeePayoutOverBudget
              ? { color: "#d32f2f", fontWeight: 600 }
              : undefined
          }
          title={
            isEmployeePayoutOverBudget
              ? "Превышение: Выплачено + К выплате больше суммы по договору"
              : undefined
          }
        >
          {toBePayedByEmployee == null
            ? "—"
            : `₸ ${formatMoney(toBePayedByEmployee)}`}
          {isEmployeePayoutOverBudget && (
            <div style={{ color: "#d32f2f", fontWeight: 600 }}>
              Макс: ₸ {formatMoney(maxAllowedPayout)}
            </div>
          )}
        </td>
        <td>
          {correctionPct == null ? "—" : `${correctionPct}%`}
          {!isReadOnly && (
            <button
              type="button"
              className="pp-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmModal({
                  open: true,
                  title: "Изменить процент выполнения?",
                  type: "percent",
                  correctionId: contractSectionsId,
                  userId: user.id,
                  userName,
                  workTypeName: correctionSectionName,
                  currentValue: String(correctionPct ?? 0),
                  newValue: String(correctionPct ?? 0),
                  estimateContract: Number(estimateContract) || 0,
                  sharePercent: 100,
                  onConfirm: (newVal) => {
                    applyCorrection({
                      rp_gp_labor_effort_id: contractSectionsId,
                      user_id: Number(user.id),
                      month,
                      year,
                      completion_percentage_after_correction: Number(newVal),
                    });
                  },
                });
              }}
              disabled={isSavingGeneralCorrection || isLockedByApproval}
              title={lockTitle || "Изменить процент"}
            >
              ✎
            </button>
          )}
        </td>
        <td
          className="pp-money"
          style={
            isCorrectionPayoutOverBudget
              ? { color: "#d32f2f", fontWeight: 600 }
              : undefined
          }
          title={
            isCorrectionPayoutOverBudget
              ? "Превышение: Выплачено + К выплате больше суммы по договору"
              : undefined
          }
        >
          {correctionSum == null ? "—" : `₸ ${formatMoney(correctionSum)}`}
          {isCorrectionPayoutOverBudget && (
            <div style={{ color: "#d32f2f", fontWeight: 600 }}>
              Макс: ₸ {formatMoney(maxAllowedPayout)}
            </div>
          )}
          {!isReadOnly && (
            <button
              type="button"
              className="pp-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmModal({
                  open: true,
                  title: "Изменить сумму к выплате?",
                  type: "sum",
                  correctionId: contractSectionsId,
                  userId: user.id,
                  userName,
                  workTypeName: correctionSectionName,
                  currentValue: String(correctionSum ?? 0),
                  newValue: String(correctionSum ?? 0),
                  estimateContract: Number(estimateContract) || 0,
                  sharePercent: 100,
                  onConfirm: (newVal) => {
                    applyCorrection({
                      rp_gp_labor_effort_id: contractSectionsId,
                      user_id: Number(user.id),
                      month,
                      year,
                      completion_sum_after_correction: Number(newVal),
                    });
                  },
                });
              }}
              disabled={isSavingGeneralCorrection || isLockedByApproval}
              title={lockTitle || "Изменить сумму"}
            >
              ✎
            </button>
          )}
        </td>
        <td>
          {correctionComment}
          {!isReadOnly && (
            <button
              type="button"
              className="pp-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                setCommentModal({
                  open: true,
                  workTypeName: `${userName} — ${correctionSectionName}`,
                  correctionId: contractSectionsId,
                  currentComment: correction?.comment || "",
                  onSave: (commentValue) => {
                    applyCorrection({
                      rp_gp_labor_effort_id: contractSectionsId,
                      user_id: Number(user.id),
                      month,
                      year,
                      comment: String(commentValue || ""),
                    });
                  },
                });
              }}
              disabled={isSavingGeneralCorrection || isLockedByApproval}
              title={lockTitle || "Комментарий"}
            >
              ✎
            </button>
          )}
        </td>
      </tr>
    </>
  );
};

const UserApprovalControls = ({
  user,
  userName,
  approval,
  approvalStatusText,
  isLockedByApproval,
  canDeleteUserApproval,
  deleteApprovalForUser,
  deletingApproval,
  isReadOnly,
}) => (
  <>
    <ApprovalStatusBadge
      approval={approval}
      approvalStatusText={approvalStatusText}
    />
    {isLockedByApproval && (
      <span
        className="pp-lock-hint"
        data-tooltip="Редактирование заблокировано: сотрудник на согласовании"
        aria-label="Редактирование заблокировано"
      >
        🔒
      </span>
    )}
    {canDeleteUserApproval && !isReadOnly && (
      <button
        type="button"
        className="pp-row-delete-approval-btn"
        onClick={(e) => {
          e.stopPropagation();
          deleteApprovalForUser(user);
        }}
        disabled={deletingApproval}
        title={`Удалить согласование для ${userName}`}
      >
        Удалить
      </button>
    )}
  </>
);

const getUserApprovalMeta = (userId, approvalByUserId, isUserOnApproval, canDeleteApprovalForUser) => {
  const approval = approvalByUserId[userId] ?? null;
  const isLockedByApproval = isUserOnApproval(userId);
  const approvalStatusText =
    approvalByUserId[userId] === undefined
      ? "Проверка..."
      : approval
        ? getApprovalStatusText(approval)
        : "Не отправлено";
  const canDeleteUserApproval = canDeleteApprovalForUser(userId);
  return { approval, isLockedByApproval, approvalStatusText, canDeleteUserApproval };
};

const getCorrectionTotal = (entries) =>
  entries.reduce((sum, data) => {
    const corrSum = Number(data?.correction?.completion_sum_after_correction);
    return Number.isFinite(corrSum) ? sum + corrSum : sum;
  }, 0);

const GeneralDesignTable = ({
  grouping = GROUP_BY_USERS,
  generalDesignLoading,
  generalDesignError,
  generalDesignRows,
  generalDesignUsers = [],
  projectsByUser = {},
  usersByContractReady = false,
  expandedGeneralProjects,
  expandedGeneralTopUsers = new Set(),
  generalUsersByContract,
  loadingGeneralUsers,
  expandedGeneralUsers,
  generalReportsByKey,
  loadingGeneralReports,
  toggleGeneralProject,
  toggleGeneralTopUser = () => {},
  toggleGeneralUser,
  savingId,
  setSavingId,
  setConfirmModal,
  setCommentModal,
  updateGeneralDesignCorrection,
  setGeneralReportsByKey,
  formatMoney,
  month,
  year,
  approvalByUserId = {},
  isUserOnApproval = () => false,
  canDeleteApprovalForUser = () => false,
  deleteApprovalForUser = () => {},
  deletingApproval = false,
  sectionTotalsState = {},
  isReadOnly = false,
}) => {
  const isUsersGrouping = grouping === GROUP_BY_USERS;
  const emptyLabel = isUsersGrouping
    ? "Нет сотрудников Генплан РП за выбранный период"
    : "Нет данных Генплан РП за выбранный период";
  const isEmpty = isUsersGrouping
    ? generalDesignUsers.length === 0
    : generalDesignRows.length === 0;

  const workDetailProps = {
    savingId,
    setSavingId,
    setConfirmModal,
    setCommentModal,
    updateGeneralDesignCorrection,
    setGeneralReportsByKey,
    formatMoney,
    month,
    year,
    isReadOnly,
  };

  const renderTotalCell = ({ hasError, isLoading, total, title }) => (
    <td
      className="pp-money pp-user-row-total"
      onClick={(e) => e.stopPropagation()}
      title={!isLoading && !hasError ? title : undefined}
    >
      {hasError ? (
        "–"
      ) : isLoading ? (
        <span className="pp-row-total-muted">…</span>
      ) : total == null ? (
        "–"
      ) : (
        <>₸ {formatMoney(total)}</>
      )}
    </td>
  );

  return (
    <div className="pp-table-wrap">
      <table className="pp-table">
        <thead>
          <tr>
            <th>{isUsersGrouping ? "Исполнитель" : "Проект"}</th>
            <th
              className="pp-money pp-user-list-sum-col"
              title={
                isUsersGrouping
                  ? "Сумма «К выплате» по корректировкам всех проектов сотрудника за месяц"
                  : "Сумма «К выплате» по корректировкам всех сотрудников этого раздела за месяц"
              }
            >
              К выплате (итого)
            </th>
          </tr>
        </thead>
        <tbody>
          {generalDesignLoading ? (
            <tr>
              <td className="pp-loading-cell" colSpan={2}>
                <Preloader />
              </td>
            </tr>
          ) : generalDesignError ? (
            <tr>
              <td className="pp-empty" colSpan={2}>
                {generalDesignError}
              </td>
            </tr>
          ) : isEmpty ? (
            <tr>
              <td className="pp-empty" colSpan={2}>
                {emptyLabel}
              </td>
            </tr>
          ) : isUsersGrouping ? (
            generalDesignUsers.map((user) => {
              const userId = Number(user.id);
              const isUserExpanded = expandedGeneralTopUsers.has(userId);
              const userProjects = projectsByUser[userId] || [];
              const userName =
                [user.surname, user.name].filter(Boolean).join(" ") ||
                `Специалист ID ${user.id}`;
              const {
                approval,
                isLockedByApproval,
                approvalStatusText,
                canDeleteUserApproval,
              } = getUserApprovalMeta(
                userId,
                approvalByUserId,
                isUserOnApproval,
                canDeleteApprovalForUser,
              );
              const userCorrectionTotal = usersByContractReady
                ? getCorrectionTotal(
                    userProjects.map(
                      (row) =>
                        generalReportsByKey[
                          `${getRpGpLaborEffortId(row)}-${userId}`
                        ],
                    ),
                  )
                : null;
              const isUserTotalLoading =
                !usersByContractReady ||
                userProjects.some((row) => {
                  const st = sectionTotalsState[getRpGpLaborEffortId(row)];
                  return st === "loading" || st === undefined;
                });
              const userTotalHasError = userProjects.some(
                (row) => sectionTotalsState[getRpGpLaborEffortId(row)] === "error",
              );

              return (
                <React.Fragment key={`gp-user-${userId}`}>
                  <tr
                    className="pp-user-row"
                    onClick={() => toggleGeneralTopUser(userId)}
                  >
                    <td>
                      <span
                        className={`pp-expand ${isUserExpanded ? "expanded" : ""}`}
                        aria-hidden
                      >
                        ›
                      </span>
                      {userName}
                      <UserApprovalControls
                        user={user}
                        userName={userName}
                        approval={approval}
                        approvalStatusText={approvalStatusText}
                        isLockedByApproval={isLockedByApproval}
                        canDeleteUserApproval={canDeleteUserApproval}
                        deleteApprovalForUser={deleteApprovalForUser}
                        deletingApproval={deletingApproval}
                        isReadOnly={isReadOnly}
                      />
                    </td>
                    {renderTotalCell({
                      hasError: userTotalHasError,
                      isLoading: isUserTotalLoading,
                      total: userCorrectionTotal,
                      title:
                        userCorrectionTotal != null
                          ? `Сумма «К выплате» по корректировкам всех проектов: ₸ ${formatMoney(userCorrectionTotal)}`
                          : undefined,
                    })}
                  </tr>
                  {isUserExpanded && (
                    <tr className="pp-detail-row">
                      <td className="pp-detail-cell" colSpan={2}>
                        {!usersByContractReady ? (
                          <div className="pp-loading-cell">
                            <Preloader />
                          </div>
                        ) : userProjects.length === 0 ? (
                          <div className="pp-empty">
                            Нет проектов с отчетами за период
                          </div>
                        ) : (
                          <table className="pp-detail-table">
                            <tbody>
                              {userProjects.map((row) => {
                                const contractSectionsId = getRpGpLaborEffortId(row);
                                const userKey = `${contractSectionsId}-${userId}`;
                                const isSectionExpanded = expandedGeneralUsers.has(userKey);
                                const rowProjectName =
                                  row?.project?.name || `ID ${row?.project_id || "—"}`;
                                const rowSectionName = row?.section?.section?.name || "—";
                                const projectLabel = `${rowProjectName} — ${rowSectionName}`;
                                return (
                                  <React.Fragment key={userKey}>
                                    <tr
                                      className="pp-project-row"
                                      onClick={() =>
                                        toggleGeneralUser(contractSectionsId, userId)
                                      }
                                    >
                                      <td colSpan={10}>
                                        <span
                                          className={`pp-expand ${isSectionExpanded ? "expanded" : ""}`}
                                          aria-hidden
                                        >
                                          ›
                                        </span>
                                        {projectLabel}
                                      </td>
                                    </tr>
                                    {isSectionExpanded && (
                                      <GeneralDesignWorkDetails
                                        {...workDetailProps}
                                        row={row}
                                        user={user}
                                        userName={userName}
                                        userKey={userKey}
                                        userReportsData={generalReportsByKey[userKey]}
                                        loadingReports={loadingGeneralReports[userKey]}
                                        isLockedByApproval={isLockedByApproval}
                                      />
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            generalDesignRows.map((row) => {
              const rowProjectName = row?.project?.name || `ID ${row?.project_id || "—"}`;
              const contractSectionsId = getRpGpLaborEffortId(row);
              const isProjectExpanded =
                !Number.isNaN(contractSectionsId) &&
                expandedGeneralProjects.has(contractSectionsId);
              const users = generalUsersByContract[contractSectionsId];
              const isLoadingUsers = loadingGeneralUsers[contractSectionsId];
              const sectionState = !Number.isNaN(contractSectionsId)
                ? sectionTotalsState[contractSectionsId]
                : undefined;
              const sectionUsersForTotal = !Number.isNaN(contractSectionsId)
                ? generalUsersByContract[contractSectionsId]
                : undefined;
              const sectionCorrectionTotal = Array.isArray(sectionUsersForTotal)
                ? getCorrectionTotal(
                    sectionUsersForTotal.map(
                      (u) => generalReportsByKey[`${contractSectionsId}-${u.id}`],
                    ),
                  )
                : null;
              const isSectionTotalLoading =
                sectionState === "loading" || sectionState === undefined;
              const sectionTotalHasError = sectionState === "error";

              return (
                <React.Fragment
                  key={`${getRpGpLaborEffortId(row) || row?.project_id || row?.project?.id || "p"}`}
                >
                  <tr
                    className="pp-user-row"
                    onClick={() => {
                      if (!Number.isNaN(contractSectionsId)) {
                        toggleGeneralProject(contractSectionsId);
                      }
                    }}
                  >
                    <td>
                      {!Number.isNaN(contractSectionsId) && (
                        <span
                          className={`pp-expand ${isProjectExpanded ? "expanded" : ""}`}
                          aria-hidden
                        >
                          ›
                        </span>
                      )}
                      {rowProjectName}
                    </td>
                    {renderTotalCell({
                      hasError: sectionTotalHasError,
                      isLoading: isSectionTotalLoading,
                      total: sectionCorrectionTotal,
                      title:
                        sectionCorrectionTotal != null
                          ? `Сумма «К выплате» по корректировкам всех сотрудников: ₸ ${formatMoney(sectionCorrectionTotal)}`
                          : undefined,
                    })}
                  </tr>
                  {isProjectExpanded && (
                    <tr className="pp-detail-row">
                      <td className="pp-detail-cell" colSpan={2}>
                        {isLoadingUsers ? (
                          <div className="pp-loading-cell">
                            <Preloader />
                          </div>
                        ) : !Array.isArray(users) || users.length === 0 ? (
                          <div className="pp-empty">
                            Нет сотрудников с отчетами за период
                          </div>
                        ) : (
                          <table className="pp-detail-table">
                            <tbody>
                              {users.map((user) => {
                                const userKey = `${contractSectionsId}-${user.id}`;
                                const isUserExpanded = expandedGeneralUsers.has(userKey);
                                const userName =
                                  [user.surname, user.name].filter(Boolean).join(" ") ||
                                  `Специалист ID ${user.id}`;
                                const {
                                  approval,
                                  isLockedByApproval,
                                  approvalStatusText,
                                  canDeleteUserApproval,
                                } = getUserApprovalMeta(
                                  user.id,
                                  approvalByUserId,
                                  isUserOnApproval,
                                  canDeleteApprovalForUser,
                                );
                                return (
                                  <React.Fragment key={userKey}>
                                    <tr
                                      className="pp-project-row"
                                      onClick={() =>
                                        toggleGeneralUser(contractSectionsId, user.id)
                                      }
                                    >
                                      <td colSpan={10}>
                                        <span
                                          className={`pp-expand ${isUserExpanded ? "expanded" : ""}`}
                                          aria-hidden
                                        >
                                          ›
                                        </span>
                                        {userName}
                                        <UserApprovalControls
                                          user={user}
                                          userName={userName}
                                          approval={approval}
                                          approvalStatusText={approvalStatusText}
                                          isLockedByApproval={isLockedByApproval}
                                          canDeleteUserApproval={canDeleteUserApproval}
                                          deleteApprovalForUser={deleteApprovalForUser}
                                          deletingApproval={deletingApproval}
                                          isReadOnly={isReadOnly}
                                        />
                                      </td>
                                    </tr>
                                    {isUserExpanded && (
                                      <GeneralDesignWorkDetails
                                        {...workDetailProps}
                                        row={row}
                                        user={user}
                                        userName={userName}
                                        userKey={userKey}
                                        userReportsData={generalReportsByKey[userKey]}
                                        loadingReports={loadingGeneralReports[userKey]}
                                        isLockedByApproval={isLockedByApproval}
                                      />
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GeneralDesignTable;
